/**
 * Stripe billing hooks for the parent PWA.
 *
 * The control plane creates the Stripe Checkout / Customer Portal session
 * server-side (Stripe secret key stays on the server), returns a hosted URL,
 * and the browser navigates there. We never touch card data locally.
 *
 * `getBillingStatus` reads the family's current subscription state so the UI
 * can nudge the parent to subscribe before their trial lapses. Same 404
 * shape as the session routes — surfaces `BillingNotAvailableError` when the
 * control plane hasn't caught up yet.
 */

import { ApiError, api } from '../state/api';

/**
 * Thrown when the CP responds 404 to a billing route — the endpoint has not
 * yet been implemented server-side. UI catches this and shows a placeholder.
 */
export class BillingNotAvailableError extends Error {
  constructor(route: string) {
    super(`billing route not available: ${route}`);
    this.name = 'BillingNotAvailableError';
  }
}

/** Checkout cadence — matches the CP's `cadence` request field. */
export type Cadence = 'monthly' | 'annual';

/**
 * Snapshot of the family's Stripe subscription state.
 *
 * `entitled` folds status + trial expiry into a single "is capture still
 * running?" flag; the UI should prefer it over inspecting the raw fields.
 * `trial_ends_at` and `current_period_end` are ISO-8601; the latter is null
 * before a subscription has ever been created (trialing customers who have
 * not entered a card).
 */
export interface BillingStatus {
  subscription_status:
    'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid';
  trial_ends_at: string;
  current_period_end: string | null;
  entitled: boolean;
  has_stripe_customer: boolean;
}

interface SessionResponse {
  url: string;
}

async function createSession(
  route: string,
  body: Record<string, unknown> = {},
): Promise<SessionResponse> {
  try {
    return await api<SessionResponse>(route, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  } catch (e) {
    // Post-plan-032 the api helper throws `ApiError` with `.status`
    // exposed. Check the status directly rather than sniffing the
    // message body, which now contains the CP's `detail` string
    // ("not found", etc.) rather than "api 404: ...".
    if (e instanceof ApiError && e.status === 404) {
      throw new BillingNotAvailableError(route);
    }
    throw e;
  }
}

/** Navigate the browser to `url`. Split out so tests can stub `window.location`. */
function redirect(url: string): void {
  window.location.href = url;
}

/**
 * Where Stripe should send the parent back after checkout / the billing
 * portal closes. We reuse the current page — parents almost always want
 * to end up back in Settings.
 */
function currentUrl(): string {
  return window.location.href;
}

/**
 * POST to the Stripe Checkout route and redirect the browser.
 *
 * `cadence` defaults to `'monthly'` so pre-existing callers keep the old
 * shape; SettingsRoute now passes the value from a monthly/annual toggle.
 * `success_url` and `cancel_url` are required by the CP contract — the
 * server rejects a body without them (422) rather than guessing.
 *
 * `opts.trialDays` (plan 030): when set, Stripe collects a card up-front
 * and starts the subscription in `trialing` with that many free days.
 * The signup-with-card intercept passes `14` here; SettingsRoute leaves
 * it unset for post-trial resubscribes.
 *
 * `opts.successPath` / `opts.cancelPath` (plan 030): override the return
 * paths so the intercept flow can send Stripe to `/checkout/success` +
 * `/checkout/cancel` rather than the caller's current URL.
 */
export async function startCheckout(
  cadence: Cadence = 'monthly',
  opts: {
    trialDays?: number;
    successPath?: string;
    cancelPath?: string;
  } = {},
): Promise<void> {
  const origin = window.location.origin;
  const success_url =
    opts.successPath !== undefined
      ? `${origin}${opts.successPath}`
      : currentUrl();
  const cancel_url =
    opts.cancelPath !== undefined
      ? `${origin}${opts.cancelPath}`
      : currentUrl();
  const body: Record<string, unknown> = {
    cadence,
    success_url,
    cancel_url,
  };
  if (opts.trialDays !== undefined) {
    body['trial_period_days'] = opts.trialDays;
  }
  const resp = await createSession('/v1/family/billing/checkout-session', body);
  redirect(resp.url);
}

/**
 * POST to the Stripe Customer Portal route and redirect the browser.
 * `return_url` is required by the CP contract.
 */
export async function openPortal(): Promise<void> {
  const resp = await createSession('/v1/family/billing/portal-session', {
    return_url: currentUrl(),
  });
  redirect(resp.url);
}

/**
 * GET the family's current billing status. Throws `BillingNotAvailableError`
 * on a 404 so early clients that ship before the CP route lands can degrade
 * to "no banner" instead of blowing up.
 */
export async function getBillingStatus(): Promise<BillingStatus> {
  try {
    return await api<BillingStatus>('/v1/family/billing/status');
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) {
      throw new BillingNotAvailableError('/v1/family/billing/status');
    }
    throw e;
  }
}
